import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { registerPWA } from './pwa'
import './lib/firebase'; // Import to initialize Firebase
// import './i18n' // Removed as it causes issues and might be redundant
import * as Sentry from "@sentry/react";
import { replayIntegration } from "@sentry/replay";
// import { configureScope } from "@sentry/react"; // Removed this import
import { Posthog } from '@capawesome/capacitor-posthog';
import Clarity from '@microsoft/clarity';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react'; // Added logrocket-react import
// import { inject as injectVercelAnalytics } from '@vercel/analytics'; // Removed, will use <Analytics /> component instead
import Hotjar from '@hotjar/browser'; // Added Hotjar import
import { H as Highlight } from 'highlight.run'; // Added Highlight frontend SDK import
import Shake from '@shakebugs/browser'; // Added Shake SDK import
import { trackEvent as posthogTrackEvent } from './lib/analytics'; // Import PostHog trackEvent

// 导入i18n配置 (这必须在其他导入之前进行)
import './lib/i18n';

// 导入主题初始化
import { initializeSystemTheme } from './utils/themeUtils';
// 导入平台检测工具
import { getPlatformInfo } from './utils/platformUtils';

// 导入语言初始化
import { store } from './store';
import { initLanguageAtom } from './atoms/shared/uiAtoms';
import { languageManager } from './services/languageManager';

// 导入 Pushy 初始化
import { initializePushyEarly } from './services/pushy';


// 导出 LogRocket Session URL 以便其他模块使用
export let logRocketSessionURL: string | undefined;

// 注册 PWA
registerPWA()

// 初始化主题系统
initializeSystemTheme()

// Android 平台检测和调试信息
const platformInfo = getPlatformInfo();

// 初始化语言系统
// 使用新的语言管理器初始化
languageManager.initialize().catch(error => {
  console.error('Failed to initialize language manager:', error);
  // 回退到原来的方法
  store.set(initLanguageAtom);
});

// 早期初始化 Pushy (仅调用 Pushy.listen())
// 根据 Capacitor 文档，应该在应用启动时就调用 Pushy.listen()
console.log('🔥 [DEBUG] About to call initializePushyEarly()');
try {
  initializePushyEarly();
  console.log('🔥 [DEBUG] initializePushyEarly() completed');
} catch (error) {
  console.error('🔥 [DEBUG] Failed to initialize Pushy early:', error);
}

if (platformInfo.isAndroid) {
  console.log('[Android Platform] App starting on Android', {
    os: platformInfo.os,
    isNative: platformInfo.isNative,
    isWeb: platformInfo.isWeb,
    userAgent: platformInfo.userAgent,
    displayMode: platformInfo.displayMode,
    isStandalone: platformInfo.isStandalone,
    isPWA: platformInfo.isPWA,
    timestamp: new Date().toISOString(),
    apiBaseUrl: import.meta.env.VITE_BACKEND_API_URL,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL
  });
}

// Initialize LogRocket
try {
  LogRocket.init('em2aac/tempo-web', {
    network: {
      requestSanitizer: (request) => {
        const blockPatterns = [
          "/ingest", // User's primary backend endpoint for PostHog
          /lgrckt-in\.com/, // LogRocket's own ingest
          /highlight\.io/,
          /sentry\.io/,
          /ingest\.sentry\.io/,
          /logrocket\.com/,
          /logrocket\.io/,
          /posthog\.com/,
          /clarity\.ms/,
          /hotjar\.com/,
        ];
        const url = typeof request === 'string' ? request : request.url;
        if (blockPatterns.some(pattern =>
            typeof pattern === 'string' ? url.includes(pattern) : pattern.test(url)
        )) {
          return null;
        }
        return request;
      },
    },
  });
  setupLogRocketReact(LogRocket); // Initialize logrocket-react
  console.log('LogRocket initialized successfully');
} catch (error) {
  console.error('Failed to initialize LogRocket:', error);
}

// 集成 LogRocket 和 Sentry & PostHog & 获取 Session URL
LogRocket.getSessionURL(sessionURL => {
  logRocketSessionURL = sessionURL; // 存储 Session URL

  const scope = Sentry.getCurrentScope();
  scope.setExtra("sessionURL", sessionURL);

  // 集成 LogRocket 和 PostHog
  posthogTrackEvent('LogRocketSessionURL', { sessionURL: sessionURL });
});

const POSTHOG_KEY = "phc_KMsNVcmkSniIZqyjMWh4ydPvx8bmc7E1jXAZpGn3X2X";

const setupPostHog = async () => {
  let posthogHost;
  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    posthogHost = "https://eu.i.posthog.com";
  } else {
    posthogHost = "/ingest";
  }

  if (POSTHOG_KEY && posthogHost) {
    try {
      await Posthog.setup({
        apiKey: POSTHOG_KEY,
        host: posthogHost,
      });
      console.log('PostHog initialized successfully. Host:', posthogHost);
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  } else {
    if (import.meta.env.DEV) {
      console.warn('PostHog Key is not set or host could not be determined. PostHog will not be initialized.');
    }
  }
};

// Call PostHog setup early in the application lifecycle
setupPostHog();

// Initialize Microsoft Clarity
const CLARITY_PROJECT_ID = "rfnttbw438"; // Your actual Clarity Project ID

if (CLARITY_PROJECT_ID && CLARITY_PROJECT_ID.trim().length > 0) {
  try {
    Clarity.init(CLARITY_PROJECT_ID);
    console.log('Microsoft Clarity initialized successfully with ID:', CLARITY_PROJECT_ID);
  } catch (error) {
    console.error('Failed to initialize Microsoft Clarity:', error);
  }
} else {
  if (import.meta.env.DEV) {
    console.warn('Microsoft Clarity Project ID is effectively missing or empty. Clarity will not be initialized.');
  }
}

// Inject Vercel Analytics - REMOVED
// if (import.meta.env.PROD) {
//   try {
//     injectVercelAnalytics();
//     console.log('Vercel Analytics injected successfully.');
//   } catch (error) {
//     console.error('Failed to inject Vercel Analytics:', error);
//   }
// }

// Initialize Hotjar
const HOTJAR_SITE_ID = 6395111;
const HOTJAR_VERSION = 6;

try {
  if (typeof window !== 'undefined') { // Ensure Hotjar runs only in the browser
    Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);
    console.log('Hotjar initialized successfully. Site ID:', HOTJAR_SITE_ID);
    // You can call Hotjar.identify() or Hotjar.tagRecording() here if needed immediately
  }
} catch (error) {
  console.error('Failed to initialize Hotjar:', error);
}

// Initialize Highlight Frontend SDK
const HIGHLIGHT_FRONTEND_PROJECT_ID = 'odz3rqpe'; // Same Project ID
if (typeof window !== 'undefined') { // Ensure runs only in browser
  try {
    // 动态设置 tracingOrigins：支持双后端域名
    const tracingOrigins = [
      'https://tempo-0y6p.onrender.com/',
      'https://api.lengditech.com/'
    ];

    Highlight.init(HIGHLIGHT_FRONTEND_PROJECT_ID, {
      serviceName: "tempo-frontend-web",
      environment: import.meta.env.MODE || 'development',
      tracingOrigins: tracingOrigins,
      networkRecording: {
        enabled: true, // Assuming you want this enabled now with blocklist
        recordHeadersAndBody: false, // Keep as false if sensitive data is a concern
        urlBlocklist: [
          "https://www.googleapis.com/identitytoolkit",
          "https://securetoken.googleapis.com",
          "/ingest",
          "lgrckt-in.com",
          "highlight.io",
          "sentry.io",
          "ingest.sentry.io",
          "logrocket.com",
          "logrocket.io",
          "posthog.com",
          "clarity.ms",
          "hotjar.com",
        ],
      },
      version: import.meta.env.VITE_APP_VERSION || 'unknown',
      manualStart: true,
    });
    console.log('Highlight Frontend SDK initialized with manualStart.');
    Highlight.start();
    console.log('Highlight.run recording started globally.');
  } catch (error) {
    console.error('Failed to initialize Highlight Frontend SDK:', error);
  }
}

// Initialize Shake SDK
/* // Shake SDK remains commented
try {
  if (typeof window !== 'undefined') { // Ensure Shake runs only in the browser
    Shake.start('3wui0XuAI8FzHSU61kQxbN10BzNmUbFY61kGXpNI5mR7A3VnQPpAEgF');
    console.log('Shake SDK initialized successfully.');
  } catch (error) {
    console.error('Failed to initialize Shake SDK:', error);
  }
*/ // This was the end of the block comment for Shake, removing it to uncomment

// 初始化 Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://0857ccaf448bc9746c75bd6f75dba014@o4509268488945664.ingest.de.sentry.io/4509268500873296',
  integrations: [
    // 启用浏览器追踪（性能监控）
    Sentry.browserTracingIntegration(),
    // 启用 Session Replay
    replayIntegration({
      // 可选：屏蔽所有文本输入，保护隐私
      maskAllText: false,
      blockAllMedia: true, // 屏蔽图像和媒体
    }),
    // Add the Profiling integration
    Sentry.browserProfilingIntegration(),
  ],
  // 设置性能监控采样率 (根据需要调整, 1.0 表示捕获所有)
  tracesSampleRate: 1.0,
  // 设置 Session Replay 采样率
  replaysSessionSampleRate: 1.0, // Change back to 100% sampling
  replaysOnErrorSampleRate: 1.0, // 发生错误时捕获会话

  // 启用调试模式（仅开发环境）
  debug: import.meta.env.DEV,

  // 设置环境标识
  environment: import.meta.env.MODE || 'development',

  // 可以设置 Release 版本，方便追踪部署
  // release: "my-project-name@" + process.env.npm_package_version,

  // 默认发送 PII 数据（根据 Sentry 建议）
  sendDefaultPii: true,

  // ADD tracePropagationTargets at the TOP LEVEL
  tracePropagationTargets: ["localhost", /^https:\/\/app\.dimpurr\.com\/api/],

  // Deny URLs to prevent Sentry from capturing its own traffic or other analytics services
  denyUrls: [
    "/ingest",
    /lgrckt-in\.com/,
    /highlight\.io/,
    /sentry\.io/, // Deny Sentry's own domains/subdomains
    /ingest\.sentry\.io/,
    /logrocket\.com/,
    /logrocket\.io/,
    /posthog\.com/,
    /clarity\.ms/,
    /hotjar\.com/,
  ],

  // Profiling
  profilesSampleRate: 1.0, // Capture 100% of transactions for profiling. Relative to tracesSampleRate.
});



ReactDOM.createRoot(document.getElementById('root')!).render(
  <App />
)